import React from 'react'
import { useLocation } from 'react-router'
import { useSmartNavigateLegacy as useNavigate } from 'components/SmartLink'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { DetailedStartupCard } from 'TEIA/components/DetailedStartupCard'
import { showModal } from 'components/popup'
import type {
  PublicAdministrationDetailState,
  PublicAdministrationFocalPointDisplay
} from 'domain/feature/hub/company/Ecosystem/publicAdministrationMappers'
import type { DateRange } from 'domain/feature/hub/company/Ecosystem/contracts'
import {
  FocalPointDivider,
  FocalPointEmptyMessage,
  FocalPointLine,
  FocalPointLinkedinLine,
  FocalPointsRoot,
  FocalPointTitle
} from './styles'

const PontosFocaisContent = ({
  pontos
}: {
  pontos: PublicAdministrationFocalPointDisplay[]
}): JSX.Element => (
  <FocalPointsRoot>
    {pontos.length === 0 ? (
      <FocalPointEmptyMessage>Nenhum ponto focal cadastrado.</FocalPointEmptyMessage>
    ) : (
      pontos.map((pf, index) => (
        <div key={`${pf.email}-${index}`}>
          <FocalPointTitle>Ponto focal {index + 1}</FocalPointTitle>
          <FocalPointLine>Nome: {pf.nome}</FocalPointLine>
          <FocalPointLine>E-mail: {pf.email}</FocalPointLine>
          <FocalPointLine>Contato: {pf.contato}</FocalPointLine>
          <FocalPointLine>Cargo: {pf.cargo}</FocalPointLine>
          <FocalPointLinkedinLine $hasDividerBelow={index < pontos.length - 1}>
            Linkedin: {pf.linkedin ?? '-'}
          </FocalPointLinkedinLine>
          {index < pontos.length - 1 && <FocalPointDivider />}
        </div>
      ))
    )}
  </FocalPointsRoot>
)

export const HubPublicAdministrationGetPage = (): JSX.Element => {
  const location = useLocation() as { state?: { publicAdministration?: PublicAdministrationDetailState; fromSubTab?: number; registerDateRange?: DateRange } }
  const navigate = useNavigate()
  const publicAdministration = location.state?.publicAdministration

  const handleVerPontosFocais = (): void => {
    const pontos = publicAdministration?.focalPointsList ?? []
    showModal('single', {
      title: 'Pontos Focais',
      message: <PontosFocaisContent pontos={pontos} />,
    })
  }

  return (
    <div className="container py-4">
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px' }}>
        <div
          role="button"
          aria-label="Voltar"
          onClick={() =>
            navigate('/hub/company/ecosystem', {
              state: {
                tab: 'publicAdministration',
                subTab: location.state?.fromSubTab ?? 0,
                registerDateRange: location.state?.registerDateRange
              }
            })
          }
          style={{
            width: '32px',
            height: '32px',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#0066B3',
            cursor: 'pointer',
          }}
        >
          <FontAwesomeIcon icon={['fas', 'arrow-left']} style={{ width: '32px', height: '32px' }} />
        </div>
        <div
          className="ecosystem-profile-title"
          style={{
            color: '#464646',
            fontSize: '28px',
            fontWeight: 700,
            lineHeight: '120%',
          }}
        >
          {publicAdministration?.title ?? '-'}
        </div>
      </div>

      <DetailedStartupCard
        variant="public-administration"
        customPrimaryColor="#0066B3"
        defaultCollapsed={false}
        logo={publicAdministration?.logo}
        pitch={publicAdministration?.pitch}
        publicAdministrationType={publicAdministration?.publicAdministrationType}
        cnpj={publicAdministration?.cnpj}
        estado={publicAdministration?.state}
        cidade={publicAdministration?.cidade}
        portePopulacional={publicAdministration?.portePopulacional}
        orcamentoAnual={publicAdministration?.orcamentoAnual}
        numeroServidores={publicAdministration?.numeroServidores}
        registrationDate={publicAdministration?.registrationDate}
        representante={publicAdministration?.representante}
        email={publicAdministration?.email}
        contato={publicAdministration?.contato}
        cpf={publicAdministration?.cpf}
        cargo={publicAdministration?.cargo}
        linkedin={publicAdministration?.linkedin}
        onVerPontosFocais={
          publicAdministration?.focalPointsList != null && publicAdministration.focalPointsList.length > 0
            ? handleVerPontosFocais
            : undefined
        }
        dirigenteMaximo={publicAdministration?.dirigenteMaximo}
        mandatoAte={publicAdministration?.mandatoAte}
        possuiAreaFormalInovacao={publicAdministration?.possuiAreaFormalInovacao}
        areaFormalInovacao={publicAdministration?.areaFormalInovacao ?? publicAdministration?.areaInovacao}
        anoCriacao={publicAdministration?.anoCriacao}
        qtdePessoasEquipe={publicAdministration?.qtdePessoasEquipe}
        temContratoGestaoICT={publicAdministration?.temContratoGestaoICT}
        realizouProcedimentosMarcoLegal={publicAdministration?.realizouProcedimentosMarcoLegal}
        verticaisImpacto={publicAdministration?.verticaisImpacto}
        objetivoEntrarPrograma={publicAdministration?.objetivoEntrarPrograma}
        existeOrcamentoCTI={publicAdministration?.existeOrcamentoCTI}
        valorEstimadoCTI={publicAdministration?.valorEstimadoCTI}
        activity={publicAdministration?.activity}
      />
    </div>
  )
}
